import { DateTime } from "luxon";

/**
 * Formats a month range with year, e.g., "January - February 2023".
 * @param startDate
 * @param endDate
 */
export function formatMonthRangeWithYear(startDate?: DateTime, endDate?: DateTime): string {
	if (!startDate || !endDate) return "";

	const startMonth = startDate.toFormat("MMM");
	const endMonth = endDate.toFormat("MMM");
	const startYear = startDate.year;
	const endYear = endDate.year;

	const yearPart = startYear === endYear ? `${startYear}` : `${startYear}-${endYear}`;
	return `${startMonth} - ${endMonth} ${yearPart}`;
}

/**
 * Formats a week range with month (and optionally year), e.g.:
 * - "15–31 Aug"
 * - "15 Aug – 23 Sept"
 * - "28 Dec – 3 Jan" or "28 Dec 2025 – 3 Jan 2026" (if showYearForCrossYear is true)
 *
 * @param startDate
 * @param endDate
 * @param showYearForCrossYear - Whether to include year if the range spans different years.
 * @param alwaysShowYear - Whether to always show the year regardless of the date range.
 */
export function formatWeekRangeWithMonth(startDate?: DateTime, endDate?: DateTime, showYearForCrossYear = false, alwaysShowYear = false): string {
	if (!startDate || !endDate) return "";

	const sameMonth = startDate.month === endDate.month && startDate.year === endDate.year;
	const sameYear = startDate.year === endDate.year;

	if (sameMonth) {
		// Example: 15–31 Aug
		return `${startDate.toFormat("d")}–${endDate.toFormat("d")} ${alwaysShowYear ? endDate.toFormat("MMM yyyy") : endDate.toFormat("MMM")}`;
	}

	if (sameYear || !showYearForCrossYear) {
		// Example: 15 Aug – 23 Sept
		return `${startDate.toFormat("d MMM")} – ${endDate.toFormat(alwaysShowYear ? "d MMM yyyy" : "d MMM")}`;
	}

	// Cross-year with year info: 28 Dec 2025 – 3 Jan 2026
	return `${startDate.toFormat("d MMM yyyy")} – ${endDate.toFormat("d MMM yyyy")}`;
}

/**
 * Formats the time difference between a reference time and a target time as a human-readable string.
 * Returns "—" if no reference time is provided.
 * Examples: "2 days 5 hours", "3 hours", "45 minutes", "0 minutes"
 */
export function formatTimeBefore(targetTime: DateTime, referenceTime: DateTime | null): string {
	if (!referenceTime) {
		return "—";
	}

	const diff = targetTime.diff(referenceTime);
	const days = Math.floor(diff.as("days"));
	const hours = Math.floor(diff.as("hours") % 24);

	if (days > 0) {
		const result = `${days} day${days > 1 ? "s" : ""}`;
		if (hours > 0) {
			return `${result} ${hours} hour${hours > 1 ? "s" : ""}`;
		}
		return result;
	}

	if (hours > 0) {
		return `${hours} hour${hours > 1 ? "s" : ""}`;
	}

	const minutes = Math.floor(diff.as("minutes"));
	return minutes > 0 ? `${minutes} minute${minutes > 1 ? "s" : ""}` : "now";
}

/**
 * Locale-aware seniority label between a hire date and now (e.g. "1 year ago", "5 months ago", "29 days ago").
 * Returns null when the date is missing or invalid. Picks the largest non-zero unit from years/months/days.
 */
export function formatSeniority(hireDate: DateTime | string | undefined | null, locale: string): string | null {
	if (!hireDate) return null;
	const dt = typeof hireDate === "string" ? DateTime.fromISO(hireDate) : hireDate;
	if (!dt.isValid) return null;
	return dt.setLocale(locale).toRelative({ unit: ["years", "months", "days"] });
}
